import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import CheckboxMultiSelect, { MultiSelectOption } from '@/components/ui/CheckboxMultiSelect/CheckboxMultiSelect';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth } from '@/api/ApiCalls';
import { FundGroupDtoIn } from '@/models/in/FundGroupDtoIn';
import { FundGroupDtoOut } from '@/models/out/FundGroupDtoOut';

interface CreateFundGroupDialogProps {
  fundOptions: MultiSelectOption<number>[];
  userOptions: MultiSelectOption<string>[];
  onClose: () => void;
  onCreated: (groupId: number) => void;
  /** Drawn as a view inside the release dialog (HAT-1489 rework), not as an overlay over it. */
  embedded?: boolean;
}

// A new group: as a view inside the release dialog (embedded, HAT-1489 rework), or as an in-place
// overlay where a page still wants one - never a separate stacked window.
const CreateFundGroupDialog = ({ fundOptions, userOptions, onClose, onCreated, embedded = false }: CreateFundGroupDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fundIds, setFundIds] = useState<number[]>([]);
  const [userEmails, setUserEmails] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError(t('fundGroups.dialog.nameRequired'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    const body: FundGroupDtoOut = { name: name.trim(), description, fundIds, userEmails };
    try {
      const created = await postAuth<FundGroupDtoIn, FundGroupDtoOut>('management/fund-groups', headers, body);
      onCreated(created.id);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      setError(message.includes('already exists') ? t('fundGroups.dialog.duplicateName') : t('fundGroups.dialog.saveError'));
      setIsSubmitting(false);
    }
  };

  const form = (
      <div className={embedded ? 'flex flex-col gap-4 w-full' : 'flex flex-col gap-4 w-full max-w-[720px] max-h-full overflow-y-auto rounded-2xl bg-(--surface-base) p-5 shadow-lg'}>
        {!embedded && <h3 className="heading-h4-semibold text-(--font-primary)">{t('fundGroups.dialog.createTitle')}</h3>}

        <div className="flex flex-col gap-1">
          <label htmlFor="new-group-name" className="body-sm-semibold text-(--font-secondary)">{t('fundGroups.columns.name')}</label>
          <input
            id="new-group-name"
            type="text"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={name}
            onChange={(e) => { setName(e.target.value); if (error) setError(null); }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="new-group-desc" className="body-sm-semibold text-(--font-secondary)">{t('fundGroups.columns.description')}</label>
          <input
            id="new-group-desc"
            type="text"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="body-sm-semibold text-(--font-secondary)">{t('fundGroups.dialog.users')}</label>
            <CheckboxMultiSelect options={userOptions} selected={userEmails} onChange={setUserEmails} maxHeightClass={embedded ? 'max-h-[360px]' : 'max-h-[220px]'} maxRendered={150} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="body-sm-semibold text-(--font-secondary)">{t('fundGroups.dialog.funds')}</label>
            <CheckboxMultiSelect options={fundOptions} selected={fundIds} onChange={setFundIds} maxHeightClass={embedded ? 'max-h-[360px]' : 'max-h-[220px]'} maxRendered={150} />
          </div>
        </div>

        {error && <p className="text-red-500 body-sm-regular">{error}</p>}

        <div className={embedded ? 'flex gap-3 justify-start mt-2' : 'flex gap-3 justify-end mt-2'}>
          {embedded ? (
            <>
              <Button variant="primary" onClick={handleCreate} disabled={isSubmitting}>{isSubmitting ? t('saving') : t('fundGroups.dialog.createAndUse')}</Button>
              <Button variant="white" onClick={onClose} disabled={isSubmitting}>{t('cancel')}</Button>
            </>
          ) : (
            <>
              <Button variant="white" onClick={onClose} disabled={isSubmitting}>{t('cancel')}</Button>
              <Button variant="primary" onClick={handleCreate} disabled={isSubmitting}>{isSubmitting ? t('saving') : t('save')}</Button>
            </>
          )}
        </div>
      </div>
  );

  if (embedded) return form;
  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}
    >
      {form}
    </div>
  );
};

export default CreateFundGroupDialog;
